import React from 'react';
import styled, { keyframes } from 'styled-components';

const Wrapper = styled.div<{ noMargin?: boolean }>`
  padding-top: ${props => (props.noMargin ? 0 : 50)}px;
  padding-bottom: ${props => (props.noMargin ? 0 : 50)}px;
`;

const LoaderWrapper = styled.div`
  position: relative;
  width: 100px;
  margin: 0 auto;
  opacity: 1;
  &:before {
    content: '';
    display: block;
    padding-top: 100%;
  }
`;

const wink = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -35px;
  }
  100% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -124px;
  }
`;

const color = keyframes`
  100%,
  0% {
    stroke: #00619e;
  }
  40% {
    stroke: #183558;
  }
  66% {
    stroke: #00aed9;
  }
  80%,
  90% {
    stroke: #f1a426;
  }
`;

const CarIcon = styled.svg<{ large?: boolean }>`
  width: ${props => (props.large ? 29 : 22)}px;
  height: ${props => (props.large ? 29 : 22)}px;
  color: var(--cvna-color-blue-4);
  fill: var(--cvna-color-blue-4);
  position: absolute;
  top: ${props => (props.large ? 35 : 39)}%;
  left: ${props => (props.large ? 35 : 39)}%;
  animation: ${wink} 1.25s ease-in-out infinite alternate;
`;

const AnimatedCircle = styled.svg<{ large?: boolean }>`
  width: ${props => (props.large ? 100 : 70)}%;
  height: ${props => (props.large ? 100 : 70)}%;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  animation: ${rotate} 2s linear infinite;
  & > circle {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
    animation:
      ${dash} 1.5s ease-in-out infinite,
      ${color} 6s ease-in-out infinite;
    stroke-linecap: round;
  }
`;

const ballClipRotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const AnimatedCircleIEFallback = styled.div<{ large?: boolean }>`
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    width: ${props => (props.large ? 100 : 70)}px;
    height: ${props => (props.large ? 100 : 70)}px;
    color: var(--cvna-color-blue-2);
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
    &:after {
      content: '';
      width: 100%;
      height: 100%;
      display: inline-block;
      border: 2px solid var(--cvna-color-blue-2);
      border-bottom-color: transparent;
      border-radius: 50%;
      background: transparent;
      animation: ${ballClipRotate} 0.75s linear infinite;
    }
  }
`;

type OwnProps = React.HTMLProps<HTMLDivElement> & {
  large?: boolean;
  noMargin?: boolean;
};

const Loader = ({ large = false, noMargin = false, ...props }: OwnProps) => (
  <Wrapper noMargin={noMargin} {...props}>
    <LoaderWrapper>
      <CarIcon large={large} viewBox={large ? '0 0 29 29' : '0 0 22 22'}>
        {large ? (
          <g>
            <path d="M14.5,2.25c5.4,0,6.24,1,6.27,1h0s-.84,1.07-6.27,1.07-6.24-1-6.27-1h0s.84-1.07,6.27-1.07M14.5,0C9.82,0,6,.73,6,3.31s3.82,3.3,8.5,3.3S23,5.88,23,3.31,19.18,0,14.5,0Z" />
            <path d="M28,17H26.17l-2.12-5.11A3.67,3.67,0,0,0,20.84,10H8.16A3.67,3.67,0,0,0,5,11.89L2.83,17H1a1,1,0,0,0,0,2H2v9a1,1,0,0,0,1,1H5a1,1,0,0,0,1-1V27H23v1a1,1,0,0,0,1,1h2a1,1,0,0,0,1-1V19h1a1,1,0,0,0,0-2ZM6.75,12.78A1.66,1.66,0,0,1,8.16,12H20.84a1.66,1.66,0,0,1,1.41.78L24,17H5ZM25,25H4V19H25Z" />
            <path d="M8,23h2a1,1,0,0,0,0-2H8a1,1,0,0,0,0,2Z" />
            <path d="M19,23h2a1,1,0,0,0,0-2H19a1,1,0,0,0,0,2Z" />
          </g>
        ) : (
          <g>
            <path d="M11,1.5c4.92,0,5.49,1,5.5,1v.06s-.58,1-5.5,1-5.49-1-5.5-1V2.47s.58-1,5.5-1M11,0C7.14,0,4,.56,4,2.5S7.14,5,11,5s7-.56,7-2.5S14.86,0,11,0Z" />
            <path d="M14.84,9a1.67,1.67,0,0,1,1.44.82L17.81,13H4.19L5.72,9.82A1.67,1.67,0,0,1,7.16,9h7.68m0-2H7.16A3.67,3.67,0,0,0,4,8.89L2,13H1a1,1,0,0,0,0,2v6H1a1,1,0,0,0,1,1H4a1,1,0,0,0,1-1H17a1,1,0,0,0,1,1h2a1,1,0,0,0,1-1h0V15a1,1,0,0,0,0-2H20l-2-4.11A3.67,3.67,0,0,0,14.84,7ZM3,19V15H19v4H3Z" />
            <path d="M7,16H6a1,1,0,0,0,0,2H7a1,1,0,0,0,0-2Z" />
            <path d="M16,16H15a1,1,0,0,0,0,2h1a1,1,0,0,0,0-2Z" />
          </g>
        )}
      </CarIcon>
      <AnimatedCircle large={large} viewBox="25 25 50 50">
        <circle
          className="path"
          cx="50"
          cy="50"
          r="20"
          fill="none"
          strokeWidth="1.5"
          strokeMiterlimit="10"
        />
      </AnimatedCircle>
      <AnimatedCircleIEFallback large={large} />
    </LoaderWrapper>
  </Wrapper>
);

export default Loader;
